import { aggregateProperties, type AggregateName } from "../domain/aggregate-events";
import { apiBase } from "./api-base";
import { isAppsInTossHost } from "./apps-in-toss";
import { z } from "zod";
import type { AnalyticsEvent, AnalyticsEventName, AnalyticsState, CosplayProject } from "../domain/types";

const allowedProperties = new Set([
  "channel", "buildVersion", "projectStatus", "count", "durationMs",
  "sourceStatus", "utmSource", "utmMedium", "utmCampaign", "route",
]);

const propertySchema = z.record(z.string(), z.union([z.string().max(80), z.number(), z.boolean()]));

export function createAnalyticsEvent(name: AnalyticsEventName, properties: Record<string, string | number | boolean> = {}, now = new Date()): AnalyticsEvent {
  const parsed = propertySchema.parse(properties);
  const forbidden = Object.keys(parsed).filter((key) => !allowedProperties.has(key));
  if (forbidden.length) throw new Error(`계측에 허용되지 않은 속성: ${forbidden.join(", ")}`);
  return { id: crypto.randomUUID(), name, occurredAt: now.toISOString(), properties: parsed };
}

export function enqueueAnalytics(state: AnalyticsState, event: AnalyticsEvent): AnalyticsState {
  if (!state.consent) return state;
  return { ...state, queue: [...state.queue, event].slice(-100) };
}

const cleanAttribution = (value: string | null) => value?.trim().slice(0, 60) || undefined;

export function parseAttribution(search: string) {
  const query = new URLSearchParams(search);
  const source = cleanAttribution(query.get("utm_source"));
  const medium = cleanAttribution(query.get("utm_medium"));
  const campaign = cleanAttribution(query.get("utm_campaign"));
  return source || medium || campaign ? { source, medium, campaign } : undefined;
}

export function projectReturnEligibility(project: CosplayProject | undefined, now = new Date()) {
  if (!project) return { eligible: false, reason: "no_project" as const };
  const nextTask = project.tasks.find((task) => task.status !== "done");
  if (!nextTask) return { eligible: true, reason: "archive_next_character" as const };
  const days = Math.ceil((new Date(project.dDay).getTime() - now.getTime()) / 86_400_000);
  return {
    eligible: true,
    reason: days <= 7 ? "d7_due_task" as const : days <= 30 ? "d30_due_task" as const : "saved_progress" as const,
    taskId: nextTask.id,
  };
}

export async function flushAnalytics(apiBase: string, state: AnalyticsState): Promise<AnalyticsState> {
  if (!state.consent || !apiBase || state.queue.length === 0) return state;
  const remaining: AnalyticsEvent[] = [];
  for (const event of state.queue) {
    try {
      const response = await fetch(`${apiBase.replace(/\/$/u, "")}/api/v1/events`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(event),
        keepalive: true,
      });
      if (!response.ok) remaining.push(event);
    } catch {
      remaining.push(event);
    }
  }
  return { ...state, queue: remaining };
}

/**
 * 동의 cohort 밖의 전체 방문 분모만 세는 필수 aggregate probe입니다.
 * cookie, stable device/user id, route, 작품/캐릭터/프로젝트 값은 보내지 않습니다.
 */
export async function sendAnonymousVisitProbe(apiBase: string, now = new Date()) {
  if (!apiBase) return false;
  try {
    const response = await fetch(`${apiBase.replace(/\/$/u, "")}/api/v1/events`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        id: crypto.randomUUID(),
        name: "anonymous_visit",
        occurredAt: now.toISOString(),
        properties: { channel: "web" },
      }),
      keepalive: true,
    });
    return response.ok;
  } catch {
    return false;
  }
}

const aggregateQueueKey = "haebolcae.aggregate-pending";
type AggregateEvent = { id: string; name: AggregateName; occurredAt: string; properties: Record<string, string> };
let memoryQueue: AggregateEvent[] = [];
let aggregateFlush: Promise<boolean> | undefined;
function readAggregateQueue(): AggregateEvent[] {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(aggregateQueueKey) || "[]");
    if (!Array.isArray(value)) return memoryQueue;
    return value.filter((row): row is AggregateEvent => Boolean(row && z.string().uuid().safeParse(row.id).success && z.string().datetime().safeParse(row.occurredAt).success && Object.hasOwn(aggregateProperties,row.name) && aggregateProperties[row.name as AggregateName].safeParse(row.properties).success)).slice(-100);
  } catch { return memoryQueue; }
}
function writeAggregateQueue(queue: AggregateEvent[]) {
  memoryQueue=queue;
  try { localStorage.setItem(aggregateQueueKey,JSON.stringify(queue)); return true; } catch { return false; }
}
export async function flushAggregateEvents() {
  if (aggregateFlush) return aggregateFlush;
  aggregateFlush=(async () => {
    let success=true;
    for (const event of readAggregateQueue()) {
      try {
        const response=await fetch(`${apiBase()}/api/v1/events`,{method:"POST",headers:{"content-type":"application/json"},keepalive:true,body:JSON.stringify(event)});
        if (response.ok) writeAggregateQueue(readAggregateQueue().filter(row=>row.id!==event.id));
        else success=false;
      } catch { success=false; }
    }
    return success;
  })();
  try { return await aggregateFlush; } finally { aggregateFlush=undefined; }
}
function queueAggregateEvent(name:AggregateName,properties:Record<string,string>) {
  const parsed=aggregateProperties[name].safeParse(properties);
  if(!parsed.success)return false;
  return writeAggregateQueue([...readAggregateQueue(),{id:crypto.randomUUID(),name,occurredAt:new Date().toISOString(),properties:parsed.data}].slice(-100));
}
export async function sendAggregateEvent(name: AggregateName, properties: Record<string, string>) {
  const parsed=aggregateProperties[name].safeParse(properties);
  if(!parsed.success)return false;
  queueAggregateEvent(name,parsed.data);
  return flushAggregateEvents();
}
export function recordFirstValue(entry: "home" | "event" | "discover" | "share") {
  try {
    if (localStorage.getItem("haebolcae.first-value")) return;
    if(queueAggregateEvent("first_value", { entry })) localStorage.setItem("haebolcae.first-value", "1");
    void flushAggregateEvents();
  } catch { /* Recording failure never prevents preparing. */ }
}
export function recordVisit() {
  void sendAggregateEvent("anonymous_visit", { channel: isAppsInTossHost() ? "toss" : "web" });
  try {
    const today = new Date().toLocaleDateString("en-CA");
    const last = localStorage.getItem("haebolcae.last-visit");
    if (last && last !== today) {
      const days = Math.round((Date.parse(today) - Date.parse(last)) / 86400000);
      if (Number.isFinite(days) && days > 0) void sendAggregateEvent("return_visit", { gap: days === 1 ? "1d" : days <= 7 ? "2-7d" : days <= 30 ? "8-30d" : "31d+" });
    }
    localStorage.setItem("haebolcae.last-visit", today);
  } catch { /* Aggregate visit remains independent of local storage. */ }
}
